import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  KEY_ENTER_COMMAND,
  KEY_SPACE_COMMAND,
} from "lexical";
import { $createEquationNode } from "../nodes/EquationNode";

/**
 * MathMarkdownPlugin
 *
 * Transforms inline ($...$) and block ($$...$$) LaTeX math expressions
 * into EquationNodes upon pressing Enter or Space.
 */
export function MathMarkdownPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handleMathTransformation = () => {
      editor.update(() => {
        const selection = $getSelection();

        if (!$isRangeSelection(selection)) {
          return;
        }

        const nodes = selection.getNodes();

        for (const node of nodes) {
          if (!$isTextNode(node)) {
            continue;
          }

          const text = node.getTextContent();

          // Check for block math first ($$...$$)
          const blockMathMatch = text.match(/\$\$([\s\S]*?)\$\$/);
          if (blockMathMatch) {
            const equation = blockMathMatch[1].trim();
            if (equation) {
              const equationNode = $createEquationNode(equation, false);
              node.replace(equationNode);
              continue;
            }
          }

          // Check for inline math ($...$)
          const inlineMathMatch = text.match(/\$([^$\n]+)\$/);
          if (inlineMathMatch) {
            const equation = inlineMathMatch[1].trim();
            if (equation && equation.length > 0) {
              const equationNode = $createEquationNode(equation, true);
              node.replace(equationNode);
              continue;
            }
          }
        }
      });
    };

    // Register commands to trigger transformation
    const unregisterEnter = editor.registerCommand(
      KEY_ENTER_COMMAND,
      () => {
        // Small delay to let the text be inserted first
        setTimeout(handleMathTransformation, 0);
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    const unregisterSpace = editor.registerCommand(
      KEY_SPACE_COMMAND,
      () => {
        // Small delay to let the space be inserted first
        setTimeout(handleMathTransformation, 0);
        return false;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      unregisterEnter();
      unregisterSpace();
    };
  }, [editor]);

  return null;
}
